import React, { useCallback, useEffect, useMemo, useState } from "react";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";

import { useClientEntityAllocationActions } from "store";
import type { IAvailableInvestor } from "store/actions/client-entity-allocation-actions/client-entity-allocation-actions";

// Backward-compatible export for existing consumers (clients-queries).
export type IInvestorOption = IAvailableInvestor;

export interface IAddEntityModalProps {
  clientId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddEntityModal: React.FC<IAddEntityModalProps> = ({
  clientId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { getAvailableInvestors, createAllocation } = useClientEntityAllocationActions();

  // ------ Data state ----------------------------------------------------
  const [availableEntities, setAvailableEntities] = useState<IAvailableInvestor[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // ------ Form state ----------------------------------------------------
  const [selectedEntity, setSelectedEntity] = useState<IAvailableInvestor | null>(null);
  const [percentage, setPercentage] = useState<string>("100");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ------ Fetch available investors when modal opens -------------------

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchInvestors = async () => {
      setIsLoadingEntities(true);
      setFetchError(null);
      try {
        const investors = await getAvailableInvestors(clientId);
        if (!cancelled) {
          setAvailableEntities(investors);
        }
      } catch (err: any) {
        if (!cancelled) {
          setFetchError(err?.message ?? "Failed to load investors.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEntities(false);
        }
      }
    };

    fetchInvestors();

    return () => {
      cancelled = true;
    };
  }, [isOpen, clientId, getAvailableInvestors]);

  // ------ Derived state ------------------------------------------------

  const allEntitiesAlreadyLinked = availableEntities.length === 0;

  // Shared-client warning — only shown when an entity is selected.
  const sharedClientsCount = selectedEntity?.shared_client_count ?? 0;
  const showSharedWarning = sharedClientsCount > 0;

  // ------ Percentage validation -----------------------------------------

  const rawValue = parseFloat(percentage);
  const isValid = !Number.isNaN(rawValue) && rawValue >= 1 && rawValue <= 100;

  // ------ Submit handler ------------------------------------------------

  const handleSubmit = useCallback(async () => {
    if (!selectedEntity || !isValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await createAllocation({
        client_id: clientId,
        investor_id: selectedEntity.id,
        allocation_percentage: rawValue
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setErrorMessage(err?.message ?? "Failed to link entity. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedEntity, isValid, isSubmitting, clientId, rawValue, createAllocation, onSuccess, onClose]);

  // ------ Cancel / reset lifecycle --------------------------------------

  const handleCancel = useCallback(() => {
    setSelectedEntity(null);
    setPercentage("100");
    setErrorMessage("");
    setFetchError(null);
    onClose();
  }, [onClose]);

  // Disable the Add button when any validation or selection condition fails.
  const isAddDisabled =
    isSubmitting ||
    allEntitiesAlreadyLinked ||
    !isValid ||
    selectedEntity === null;

  // Reset form state when the modal is re-opened.
  useEffect(() => {
    if (isOpen) {
      setSelectedEntity(null);
      setPercentage("100");
      setErrorMessage("");
      setFetchError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // ------ Render -------------------------------------------------------

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "10px" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "50px",
          bgcolor: "#edf1f9",
          color: "#0a3142",
          borderRadius: "10px 10px 0 0",
          px: "34px"
        }}
      >
        <Typography>Link Entity</Typography>
        <IconButton onClick={handleCancel} sx={{ color: "#5e7a95" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ py: "30px", px: "45px !important" }}>
        {allEntitiesAlreadyLinked && !isLoadingEntities ? (
          <Typography sx={{ color: "#5e7a95", textAlign: "center", py: 4 }}>
            All investment entities in this account are already linked to this
            client.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {isLoadingEntities ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Stack>
            ) : fetchError ? (
              <Typography sx={{ color: "#d32f2f", textAlign: "center", py: 2 }}>
                {fetchError}
              </Typography>
            ) : (
              <>
                {/* Entity selector */}
                <Autocomplete<IAvailableInvestor, false, true, false>
                  options={availableEntities}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedEntity ?? undefined}
                  onChange={(_event, newValue) => {
                    setSelectedEntity(newValue as IAvailableInvestor | null);
                    setErrorMessage(""); // Clear error when user picks a different entity.
                  }}
                  disabled={isSubmitting}
                  disableClearable
                  noOptionsText="No available entities"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Entity name"
                      required
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": "entity name"
                      }}
                    />
                  )}
                />

                {/* Shared-client warning banner */}
                {showSharedWarning && (
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{
                      bgcolor: "#fff8e1",
                      border: "1px solid #ffe082",
                      borderRadius: 1,
                      px: 2,
                      py: 1.5
                    }}
                  >
                    <WarningAmberIcon
                      sx={{ fontSize: 20, color: "#f57f17", mt: 0.2, flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ color: "#e65100" }}>
                      This entity is also linked to {sharedClientsCount} other
                      client(s). Files and deals associated with it will be visible
                      in all linked clients&apos; workspaces.
                    </Typography>
                  </Stack>
                )}

                {/* Percentage number input */}
                <TextField
                  label="Allocation %"
                  type="number"
                  fullWidth
                  value={percentage}
                  onChange={(e) => {
                    setPercentage(e.target.value);
                    setErrorMessage(""); // Clear validation error while typing.
                  }}
                  disabled={isSubmitting}
                  inputProps={{
                    "aria-label": "allocation percentage",
                    min: 1,
                    max: 100
                  }}
                  error={!isValid}
                  helperText={
                    !isValid
                      ? "Enter a number between 1 and 100."
                      : "Percentage value (1-100)"
                  }
                />
              </>
            )}
          </Stack>
        )}

        {/* Inline error message */}
        {errorMessage && !allEntitiesAlreadyLinked && (
          <Typography
            sx={{
              mt: 2,
              color: "#d32f2f",
              fontSize: "14px"
            }}
          >
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: "16px 24px", gap: 1 }}>
        <MuiButton
          onClick={handleCancel}
          variant="outlined"
          sx={{ textTransform: "none" }}
          disabled={isSubmitting}
        >
          Cancel
        </MuiButton>
        <MuiButton
          onClick={handleSubmit}
          variant="contained"
          disabled={isAddDisabled}
          sx={{ textTransform: "none" }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add"
          )}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};
